import React, { useState, useContext } from "react";
import { VoteOnList } from "../api-requests/Votes";
import { getBearerHeaders } from "../helpers/GetBearerHeaders";
import { UserContext } from "../context/UserContext";
import {
  ListVoteType,
  ListVoteTotals,
  ListVoteEnhancedTotals,
  ListVotePercentages,
} from "../types/votes";
import { calculateVotePercentages } from "../helpers/VoteCalculations";

interface ListVoteBarProps {
  listId: string;
  voteTotals: ListVoteTotals;
  currentUserVote: ListVoteType | null;
  onVoteUpdate: (
    totals: ListVoteTotals,
    userVote: ListVoteType,
    enhancedData?: {
      listScore: number;
      listScoreLabel: string;
      listVoteTotals: ListVoteEnhancedTotals;
      listVotePercentages: ListVotePercentages;
    },
  ) => void;
}

const ListVoteBar: React.FC<ListVoteBarProps> = ({
  listId,
  voteTotals,
  currentUserVote,
  onVoteUpdate,
}) => {
  const { user } = useContext(UserContext);
  const [isLoading, setIsLoading] = useState<string | null>(null);

  const handleVote = async (voteType: ListVoteType) => {
    if (!user) {
      // TODO: Show login prompt using existing auth pattern
      alert("Please log in to vote");
      return;
    }

    if (isLoading) return;

    setIsLoading(voteType);
    try {
      const headers = getBearerHeaders();
      const response = await VoteOnList(listId, voteType, headers);

      // Pass enhanced data if available, fallback to basic data
      const enhancedData =
        response.listScore !== undefined
          ? {
              listScore: response.listScore,
              listScoreLabel: response.listScoreLabel,
              listVoteTotals: response.listVoteTotals,
              listVotePercentages: response.listVotePercentages,
            }
          : undefined;

      onVoteUpdate(response.voteTotals, voteType, enhancedData);
    } catch (error) {
      console.error("Error voting on list:", error);
      // TODO: Show error message using existing error pattern
    } finally {
      setIsLoading(null);
    }
  };

  const percentages = calculateVotePercentages(voteTotals);
  const totalVotes = voteTotals.valid + voteTotals.debatable + voteTotals.trash;

  return (
    <div className="mt-2  ">
      <div className="separator-fade" />

      <div className="flex flex-col sm:flex-row gap-4 mb-4 items-center">
        <h3 className="text-sm">Rate this list?</h3>
        <button
          onClick={() => handleVote("valid")}
          disabled={isLoading !== null}
          className={`btn-rank ${
            currentUserVote === "valid"
              ? "bg-green-600 text-white border-2 border-green-400"
              : "bg-gray-700 text-gray-200 hover:bg-green-700 border-2 border-transparent hover:border-green-400"
          } ${isLoading === "valid" ? "opacity-50 cursor-not-allowed" : ""}`}
        >
          {isLoading === "valid"
            ? "..."
            : `🔥 Valid ${totalVotes > 0 ? `(${percentages.validPct}%)` : ""}`}
        </button>

        <button
          onClick={() => handleVote("debatable")}
          disabled={isLoading !== null}
          className={`btn-rank ${
            currentUserVote === "debatable"
              ? "bg-yellow-600 text-white border-2 border-yellow-400"
              : "bg-gray-700 text-gray-200 hover:bg-yellow-700 border-2 border-transparent hover:border-yellow-400"
          } ${isLoading === "debatable" ? "opacity-50 cursor-not-allowed" : ""}`}
        >
          {isLoading === "debatable"
            ? "..."
            : `🤔 Debatable ${totalVotes > 0 ? `(${percentages.debatablePct}%)` : ""}`}
        </button>

        <button
          onClick={() => handleVote("trash")}
          disabled={isLoading !== null}
          className={`btn-rank ${
            currentUserVote === "trash"
              ? "bg-red-600 text-white border-2 border-red-400"
              : "bg-gray-700 text-gray-200 hover:bg-red-700 border-2 border-transparent hover:border-red-400"
          } ${isLoading === "trash" ? "opacity-50 cursor-not-allowed" : ""}`}
        >
          {isLoading === "trash"
            ? "..."
            : `🗑️ Trash ${totalVotes > 0 ? `(${percentages.trashPct}%)` : ""}`}
        </button>
      </div>
    </div>
  );
};

export default ListVoteBar;
